// La timeline del Estudio se mueve sola cuando un arrastre llega al borde —
// ADR-2026-11-02-la-timeline-sigue-al-cursor.
//
// Antes, arrastrar el cursor (o un clip, o la manija de un recorte) fuera de lo visible
// dejaba la timeline quieta: el tiempo seguía al mouse fuera de la vista y "lo perdías"
// (reporte de Nacho, 2/11). Es el gesto de cualquier editor: cerca del borde la cinta
// corre, y más rápido cuanto más te acercás o te pasás.

/** Ancho (px) de la franja junto a cada borde donde el arrastre empieza a mover la cinta. */
export const ZONA_DE_AUTOSCROLL_PX = 56;
/** Velocidad máxima (px por frame, ~60 fps): con el puntero en el borde o pasado. */
export const VELOCIDAD_MAXIMA_PX = 22;
/** Cuánto hay que mover el puntero para que un "apretar" cuente como arrastre. */
export const UMBRAL_DE_ARRASTRE_PX = 4;

/**
 * Cuántos píxeles correr la cinta en este frame: negativo hacia la izquierda, positivo
 * hacia la derecha, 0 si el puntero está lejos de los bordes. Crece de forma cuadrática
 * dentro de la zona (arranca suave) y se topea en la máxima al llegar al borde o pasarlo.
 */
export function velocidadDeAutoscroll(
  x: number,
  izquierda: number,
  derecha: number,
  zona: number = ZONA_DE_AUTOSCROLL_PX,
  maxima: number = VELOCIDAD_MAXIMA_PX,
): number {
  if (!Number.isFinite(x) || derecha - izquierda <= zona * 2) return 0;
  const fuerza = (adentro: number) => {
    const t = Math.min(1, Math.max(0, adentro / zona));
    return Math.max(1, Math.round(maxima * t * t));
  };
  if (x < izquierda + zona) return -fuerza(izquierda + zona - x);
  if (x > derecha - zona) return fuerza(x - (derecha - zona));
  return 0;
}

/** ¿El puntero se movió lo suficiente desde donde apretó como para que sea un arrastre? */
export function esArrastre(
  inicio: { x: number; y: number },
  ahora: { x: number; y: number },
  umbral: number = UMBRAL_DE_ARRASTRE_PX,
): boolean {
  return Math.hypot(ahora.x - inicio.x, ahora.y - inicio.y) >= umbral;
}

/**
 * Corre un bucle de `requestAnimationFrame` que mueve el `scrollLeft` del contenedor
 * mientras el puntero esté cerca de un borde, y avisa con `alDesplazar` cada vez que la
 * cinta se movió (para que el arrastre recalcule su posición con el scroll nuevo: el
 * puntero quieto sobre el borde también tiene que seguir corriendo el cursor o el clip).
 * Devuelve la función que lo frena.
 */
export function iniciarAutoscroll(opciones: {
  contenedor: () => HTMLElement | null;
  /** X del puntero en pantalla, o null si todavía no cuenta como arrastre. */
  punteroX: () => number | null;
  alDesplazar: () => void;
}): () => void {
  let vivo = true;
  let raf = 0;
  const paso = () => {
    if (!vivo) return;
    const el = opciones.contenedor();
    const x = opciones.punteroX();
    if (el && x !== null) {
      const rect = el.getBoundingClientRect();
      const v = velocidadDeAutoscroll(x, rect.left, rect.right);
      if (v !== 0) {
        const antes = el.scrollLeft;
        el.scrollLeft = antes + v;
        if (el.scrollLeft !== antes) opciones.alDesplazar();
      }
    }
    raf = requestAnimationFrame(paso);
  };
  raf = requestAnimationFrame(paso);
  return () => {
    vivo = false;
    cancelAnimationFrame(raf);
  };
}

/**
 * Cuánto scroll hace falta para que el cursor vuelva a la vista, o null si ya se ve.
 * Cuando se sale, la cinta "salta de página" y deja el cursor cerca del borde izquierdo
 * (como Premiere al reproducir), en vez de seguirlo pegado al borde frame a frame.
 */
export function scrollParaVerElCursor(
  /** X del cursor en el CONTENIDO de la timeline (px desde el arranque, padding incluido). */
  xDelCursor: number,
  scrollLeft: number,
  anchoVisible: number,
  margen = 24,
): number | null {
  if (anchoVisible <= margen * 2) return null;
  const xEnPantalla = xDelCursor - scrollLeft;
  if (xEnPantalla >= margen && xEnPantalla <= anchoVisible - margen) return null;
  return Math.max(0, Math.round(xDelCursor - anchoVisible * 0.1));
}
